/**
 * Message thread for a roundtable conversation (§7h).
 *
 * Reads from store.activeRoundtable (populated by the Roundtable page
 * during the streaming lifecycle or hydrated from a GET /conversations
 * fetch) and renders each message via RoundtableMessage.
 *
 * Accent colour assignment is stable across the whole conversation:
 * each personaId gets the accent matching its position in the stored
 * `personaIds` array (1st = accent-1, 2nd = accent-2, 3rd = accent-3).
 * Messages from the same persona in different turns share the accent.
 *
 * Auto-scrolls to the bottom whenever new tokens arrive — same pattern
 * as single Chat.jsx.
 */

import { useEffect, useMemo, useRef } from "react";
import RoundtableMessage from "./RoundtableMessage.jsx";
import EmptyState from "./EmptyState.jsx";

export default function RoundtableThread({ messages, personaIds, personas }) {
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Resolve each personaId in the panel to its full persona record,
  // keyed for O(1) lookup during render. Memoised on personas +
  // personaIds so a new Map isn't allocated every time tokens stream
  // in — the map only needs to rebuild when the panel changes or
  // the personas list finishes loading.
  const personaMap = useMemo(
    () =>
      new Map(
        (personas || [])
          .filter((p) => personaIds.includes(p.id))
          .map((p) => [p.id, p]),
      ),
    [personas, personaIds],
  );

  // Stable accent index per personaId = 1-based position in the panel.
  const accentIndexFor = (personaId) => {
    const idx = personaIds.indexOf(personaId);
    return idx >= 0 ? idx + 1 : null;
  };

  if (!messages || messages.length === 0) {
    return (
      <EmptyState
        title="Ready for your question"
        description="Send a message to start the debate. Each panellist will respond in the order you selected them."
      />
    );
  }

  return (
    <div className="roundtable-thread">
      {messages.map((m, i) => (
        <RoundtableMessage
          key={i}
          role={m.role}
          content={m.content}
          persona={m.personaId ? personaMap.get(m.personaId) : undefined}
          accentIndex={m.personaId ? accentIndexFor(m.personaId) : null}
          streaming={m.streaming}
          error={m.error}
        />
      ))}
      <div ref={endRef} />
    </div>
  );
}
